import {toast} from '@wandb/weave/common/components/elements/Toast';
import throttle from 'lodash/throttle';
import React, {Dispatch, SetStateAction} from 'react';
import {Link} from 'react-router-dom';

import {CustomWeaveTypePayload} from '../../../typeViews/customWeaveType.types';
import {Message, ToolCall} from '../../ChatView/types';
import {useGetTraceServerClientContext} from '../../wfReactInterface/traceServerClientContext';
import {
  CompletionChunk,
  CompletionsCreateRes,
  CompletionsCreateStreamRes,
  ContentChunk,
} from '../../wfReactInterface/traceServerClientTypes';
import {LLM_MAX_TOKENS} from '../llmMaxTokens';
import {
  OptionalLitellmCompletionResponse,
  OptionalTraceCallSchema,
  PlaygroundState,
} from '../types';
import {PlaygroundMessageRole} from '../types';
import {getInputFromPlaygroundState} from '../usePlaygroundState';
import {clearTraceCall} from './useChatFunctions';
import {SetPlaygroundStateFieldFunctionType} from './useChatFunctions';
import {useImageGenerationFunctions} from './useImageGenerationFunctions';

// Helper functions for tool call processing
/**
 * Merges streaming tool call deltas into existing tool calls array
 */
const mergeToolCallDelta = (
  toolCalls: Array<ToolCall>,
  toolCallDelta: Array<ToolCall>
) => {
  toolCallDelta.forEach((toolCall: ToolCall) => {
    // Find existing tool call by index if ID is null
    const existingToolCall = toolCalls.find(
      (t: ToolCall) => toolCall.id && t.id === toolCall.id
    );

    if (existingToolCall) {
      if (toolCall.function?.name) {
        existingToolCall.function = existingToolCall.function || {};
        existingToolCall.function.name = toolCall.function.name;
      }
      if (toolCall.function?.arguments) {
        existingToolCall.function = existingToolCall.function || {};
        existingToolCall.function.arguments =
          (existingToolCall.function.arguments || '') +
          toolCall.function.arguments;
      }
    } else {
      // Add new tool call
      const newToolCall = {
        id: toolCall.id || '',
        type: toolCall.type || 'function',
        function: {
          name: toolCall.function?.name || '',
          arguments: toolCall.function?.arguments || '',
        },
      };
      toolCalls.push(newToolCall);
    }
  });
};

/**
 * Validates tool calls have complete data and parseable JSON arguments
 */
const hasValidToolCalls = (toolCalls: ToolCall[]): boolean => {
  return toolCalls.some((tc: ToolCall) => {
    if (!tc.id || !tc.function?.name || !tc.function?.arguments) return false;
    try {
      JSON.parse(tc.function.arguments);
      return true;
    } catch {
      return false;
    }
  });
};

/**
 * Creates final completion response from aggregated streaming data
 */
const createFinalResponse = (
  aggregatedRes: OptionalLitellmCompletionResponse,
  weaveCallId?: string
): CompletionsCreateRes => {
  return {
    response: {
      ...aggregatedRes,
      choices:
        aggregatedRes.choices?.map((choice, index) => ({
          ...choice,
          index,
          message: {
            ...choice?.message,
            tool_calls: choice?.message?.tool_calls || [],
            reasoning_content: choice?.message?.reasoning_content || '',
          },
        })) || [],
    },
    weave_call_id: weaveCallId,
  } as CompletionsCreateRes;
};

/**
 * Creates initial empty response structure for streaming accumulation
 */
const createAggregatedResponse = (
  nTimes: number = 1
): OptionalLitellmCompletionResponse => ({
  choices: Array.from({length: nTimes}, (_, index) => ({
    index,
    message: {
      role: 'assistant',
      content: '',
      reasoning_content: '',
      tool_calls: [],
    },
  })),
});

export const useChatCompletionFunctions = (
  setPlaygroundStates: Dispatch<SetStateAction<PlaygroundState[]>>,
  setPlaygroundStateField: SetPlaygroundStateFieldFunctionType,
  playgroundStates: PlaygroundState[],
  entity: string,
  project: string,
  setChatText: (text: string) => void,
  variableValues?: Record<string, string>,
  variableMap?: Record<string, number[]>
) => {
  const getTsClient = useGetTraceServerClientContext();
  const {generateImage} = useImageGenerationFunctions();

  // Store abort controllers for active requests
  const abortControllersRef = React.useRef<Map<number, AbortController>>(
    new Map()
  );

  const makeImageGenerationRequest = async (
    callIndex: number,
    updatedStates: PlaygroundState[],
    prompt: string
  ): Promise<any> => {
    setPlaygroundStateField(callIndex, 'loading', true);
    const state = updatedStates[callIndex];
    const modelName = state.model;

    // Build inputs object
    const inputs: any = {
      model: modelName,
      prompt,
      n: state.nTimes,
    };

    // Add prompt to inputs only if a prompt is being used AND it has not been modified
    if (state.prompt?.promptRef && !state.prompt?.isModified) {
      inputs.prompt = state.prompt.promptRef;
    }

    const req: any = {
      project_id: `${entity}/${project}`,
      inputs,
      track_llm_call: state.trackLLMCall,
    };

    return generateImage(req);
  };

  // Streaming variant of completion request. Returns the parsed chunk array.
  const makeCompletionStreamRequest = (
    callIndex: number,
    updatedStates: PlaygroundState[],
    onChunk: (chunk: CompletionChunk) => void
  ): Promise<CompletionsCreateStreamRes> => {
    const state = updatedStates[callIndex];

    const inputs: any = getInputFromPlaygroundState(state);

    // Add prompt to inputs only if a prompt is being used AND it has not been modified
    if (state.prompt?.promptRef && !state.prompt?.isModified) {
      inputs.prompt = state.prompt.promptRef;
    } else if (state.prompt?.messages && state.prompt.messages.length > 0) {
      // If there are prompt messages and (no prompt ref OR prompt is modified),
      // prepend the prompt messages to the conversation
      inputs.messages = [...state.prompt.messages, ...inputs.messages];
    }

    if (variableValues && Object.keys(variableValues).length > 0) {
      if (variableMap) {
        inputs.template_vars = Object.keys(variableValues)
          .filter(key => key in variableMap)
          .reduce(
            (acc, key) => {
              acc[key] = variableValues[key];
              return acc;
            },
            {} as Record<string, string>
          );
      } else {
        inputs.template_vars = variableValues;
      }
    }

    // Create abort controller for this request
    const abortController = new AbortController();
    abortControllersRef.current.set(callIndex, abortController);

    return getTsClient().completionsCreateStream(
      {
        project_id: `${entity}/${project}`,
        inputs,
        track_llm_call: state.trackLLMCall,
      },
      onChunk,
      abortController.signal
    );
  };

  const handleErrorsAndUpdate = async (
    response: CompletionsCreateRes | null,
    callIndex: number
  ): Promise<boolean> => {
    const hasMissingLLMApiKey = handleMissingLLMApiKey(response, entity);
    const hasError = handleErrorResponse(response);

    if (hasMissingLLMApiKey || hasError) {
      return false;
    }

    setPlaygroundStates(prevState => {
      const newState = prevState.map((state, index) => {
        if (index === callIndex) {
          return handleUpdateCallWithResponse(state, response);
        }
        return state;
      });
      return newState;
    });
    return true;
  };

  /**
   * Common setup logic for both send and retry operations.
   * Determines which chats to update, creates messages, and prepares state.
   * Handles both new message scenarios (send) and retry scenarios (messageIndex provided).
   */
  const setupCallRequest = ({
    role,
    chatText,
    callIndex,
    content,
    toolCallId,
    messageIndex,
    choiceIndex,
  }: {
    role?: PlaygroundMessageRole;
    chatText?: string;
    callIndex?: number;
    content?: string;
    toolCallId?: string;
    messageIndex?: number;
    choiceIndex?: number;
  }) => {
    const chatsToUpdate =
      callIndex !== undefined ? [callIndex] : playgroundStates.map((_, i) => i);

    const newMessageContent = content || chatText || '';
    const newMessage = role
      ? createMessage(role, newMessageContent, toolCallId)
      : undefined;

    const updatedStates = [...playgroundStates];

    chatsToUpdate.forEach(idx => {
      let updatedState: PlaygroundState;

      if (messageIndex !== undefined) {
        // Retry scenario
        updatedState = JSON.parse(JSON.stringify(playgroundStates[idx]));
        if (choiceIndex !== undefined) {
          // Choice retry - keep all messages, just regenerate the response
          updatedState = appendChoiceToMessages(updatedState, choiceIndex);
        } else {
          // Message retry - slice messages to messageIndex
          if (updatedState.traceCall?.inputs?.messages) {
            updatedState.traceCall.inputs.messages =
              updatedState.traceCall.inputs.messages.slice(0, messageIndex + 1);
            updatedState.traceCall.output = undefined;
          }
        }
      } else {
        // Send scenario - append choice and add new message
        updatedState = appendChoiceToMessages(playgroundStates[idx]);
        if (newMessage && updatedState.traceCall?.inputs?.messages) {
          updatedState.traceCall.inputs.messages.push(newMessage);
        }
      }

      updatedState.loading = true;
      updatedStates[idx] = filterNullMessages(updatedState);
    });

    return {chatsToUpdate, updatedStates};
  };

  /**
   * Handles promise execution with unified error handling and loading state management
   */
  const withErrorHandling = async (
    chatsToUpdate: number[],
    promiseFactory: (idx: number) => Promise<{idx: number; success: boolean}>
  ) => {
    try {
      const promises = chatsToUpdate.map(promiseFactory);
      await Promise.all(promises);
    } catch (error) {
      console.error('Error processing completion:', error);
      // Reset all loading states on global error
      chatsToUpdate.forEach(idx => {
        setPlaygroundStateField(idx, 'loading', false);
      });
    }
  };

  /*
   * Streaming-friendly send. Uses makeCompletionStreamRequest and updates
   * state as each chunk arrives so the UI can reflect partial generation.
   */
  const handleStreamSend = async (
    role: PlaygroundMessageRole,
    chatText: string,
    callIndex?: number,
    content?: string,
    toolCallId?: string
  ) => {
    const {chatsToUpdate, updatedStates} = setupCallRequest({
      role,
      chatText,
      callIndex,
      content,
      toolCallId,
    });

    // Update state with the new messages before starting API requests
    setPlaygroundStates(updatedStates);
    setChatText('');

    await withErrorHandling(chatsToUpdate, async idx => {
      // Check if the model is gpt-image-1 (image generation model)
      const modelName = updatedStates[idx].model;
      const modelConfig = LLM_MAX_TOKENS[modelName];
      const isImageModel = modelConfig?.is_image_model === true;

      if (isImageModel) {
        // Get the last user message as the prompt
        const lastUserMessage = updatedStates[idx].traceCall?.inputs?.messages
          ?.slice()
          .reverse()
          .find((msg: any) => msg.role === 'user');

        const prompt =
          typeof lastUserMessage?.content === 'string'
            ? lastUserMessage.content
            : chatText;

        try {
          const response = await makeImageGenerationRequest(
            idx,
            updatedStates,
            prompt
          );

          const success = await handleErrorsAndUpdate(response, idx);
          if (!success) {
            setPlaygroundStateField(idx, 'loading', false);
          }
          return {idx, success};
        } catch (error) {
          console.error(
            `Error processing image generation for chat ${idx}:`,
            error
          );
          setPlaygroundStateField(idx, 'loading', false);
          return {idx, success: false};
        }
      }

      // Aggregate content incrementally per chat
      let aggregatedRes = createAggregatedResponse(updatedStates[idx].nTimes);

      try {
        // Create throttled update callback once for this stream
        const throttledUpdate = throttle(
          () => updatePlaygroundStateWithStream(idx, aggregatedRes),
          80 // 80-ms throttle cadence feels responsive without blocking
        );

        const res = await makeCompletionStreamRequest(
          idx,
          updatedStates,
          chunk => {
            processStreamChunk(chunk, aggregatedRes, throttledUpdate);
          }
        );

        const finalResponse = createFinalResponse(
          aggregatedRes,
          res?.weave_call_id
        );

        await handleErrorsAndUpdate(finalResponse, idx);
        setPlaygroundStateField(idx, 'loading', false);
        // Clean up abort controller
        abortControllersRef.current.delete(idx);
        return {idx, success: true};
      } catch (err) {
        console.error(`Error streaming completion for chat ${idx}:`, err);
        setPlaygroundStateField(idx, 'loading', false);
        // Clean up abort controller
        abortControllersRef.current.delete(idx);
        return {idx, success: false};
      }
    });
  };

  /*
   * Streaming-aware retry. Performs same state modifications as handleRetry
   * but uses the streaming completions endpoint so the UI can progressively
   * show the regenerated answer.
   */
  const handleRetryStream = async (
    callIndex: number,
    messageIndex: number,
    choiceIndex?: number
  ) => {
    try {
      const {updatedStates} = setupCallRequest({
        callIndex,
        messageIndex,
        choiceIndex,
      });

      setPlaygroundStates(updatedStates);

      // Check if the model is an image generation model
      const modelName = updatedStates[callIndex].model;
      const modelConfig = LLM_MAX_TOKENS[modelName];
      const isImageModel = modelConfig?.is_image_model === true;

      if (isImageModel) {
        // Get the last user message as the prompt
        const lastUserMessage = updatedStates[
          callIndex
        ].traceCall?.inputs?.messages
          ?.slice()
          .reverse()
          .find((msg: any) => msg.role === 'user');

        const prompt =
          typeof lastUserMessage?.content === 'string'
            ? lastUserMessage.content
            : '';

        try {
          const response = await makeImageGenerationRequest(
            callIndex,
            updatedStates,
            prompt
          );

          const success = await handleErrorsAndUpdate(response, callIndex);
          if (!success) {
            setPlaygroundStateField(callIndex, 'loading', false);
          }
          return;
        } catch (error) {
          console.error(
            `Error processing image generation
             retry for chat ${callIndex}:`,
            error
          );
          setPlaygroundStateField(callIndex, 'loading', false);
          return;
        }
      }

      // Regular chat completion streaming retry
      // Accumulate chunks into a single response object while streaming
      let aggregatedRes = createAggregatedResponse(
        updatedStates[callIndex].nTimes
      );

      // Create throttled update callback once for this stream
      const throttledUpdate = throttle(
        () => updatePlaygroundStateWithStream(callIndex, aggregatedRes),
        80 // 80-ms throttle cadence feels responsive without blocking
      );

      // Kick off streaming request
      const res = await makeCompletionStreamRequest(
        callIndex,
        updatedStates,
        chunk => {
          processStreamChunk(chunk, aggregatedRes, throttledUpdate);
        }
      );

      const finalResponse = createFinalResponse(
        aggregatedRes,
        res?.weave_call_id
      );

      const success = await handleErrorsAndUpdate(finalResponse, callIndex);
      if (!success) {
        setPlaygroundStateField(callIndex, 'loading', false);
      }
      // Clean up abort controller
      abortControllersRef.current.delete(callIndex);
    } catch (error) {
      console.error('Error processing streamed completion retry:', error);
      setPlaygroundStateField(callIndex, 'loading', false);
      // Clean up abort controller
      abortControllersRef.current.delete(callIndex);
    }
  };

  /**
   * Updates playground state with streaming response data, preserving existing tool calls
   */
  const updatePlaygroundStateWithStream = (
    idx: number,
    aggregatedRes: OptionalLitellmCompletionResponse
  ) => {
    setPlaygroundStates(prev => {
      const next = [...prev];
      const tgt = next[idx];

      // Map over all choices to preserve each one's state
      const updatedChoices =
        aggregatedRes.choices?.map((choice, choiceIdx) => {
          const existingToolCalls =
            (tgt.traceCall?.output as any)?.choices?.[choiceIdx]?.message
              ?.tool_calls || [];
          const currentToolCalls = choice?.message?.tool_calls || [];

          return {
            ...choice,
            index: choiceIdx,
            message: {
              ...choice?.message,
              tool_calls:
                currentToolCalls.length > 0
                  ? currentToolCalls
                  : existingToolCalls,
              reasoning_content: choice?.message?.reasoning_content || '',
            },
          };
        }) || [];

      tgt.traceCall = {
        ...tgt.traceCall,
        inputs: {
          ...tgt.traceCall?.inputs,
          messages: tgt.traceCall?.inputs?.messages,
        },
        output: {
          ...aggregatedRes,
          choices: updatedChoices,
        },
      } as OptionalTraceCallSchema;
      return next;
    });
  };

  /**
   * Processes streaming completion chunks, handling both content and tool calls.
   * Accumulates content and merges tool call deltas, calling throttled updateCallback when data is ready.
   */
  const processStreamChunk = (
    chunk: CompletionChunk,
    aggregatedRes: OptionalLitellmCompletionResponse,
    throttledUpdate: () => void
  ) => {
    // Handle error chunk
    if ((chunk as any).error) {
      toast((chunk as any).error, {type: 'error'});
    }

    const contentChunk = chunk as ContentChunk;

    // Process all choices in the chunk
    contentChunk?.choices?.forEach(choice => {
      const choiceIndex = choice.index;
      const delta = choice.delta;

      // Ensure we have a choice at this index
      if (!aggregatedRes.choices?.[choiceIndex]) {
        return;
      }

      const targetChoice = aggregatedRes.choices[choiceIndex];
      if (!targetChoice.message) {
        return;
      }

      let shouldUpdate = false;

      // Handle content updates
      if (delta?.content) {
        targetChoice.message.content += delta.content;
        shouldUpdate = true;
      }

      // Handle reasoning content updates
      if (delta?.reasoning_content) {
        targetChoice.message.reasoning_content += delta.reasoning_content;
        shouldUpdate = true;
      }

      // Handle tool calls
      if (delta?.tool_calls) {
        if (!targetChoice.message.tool_calls) {
          targetChoice.message.tool_calls = [];
        }
        // Merge tool calls from delta into existing tool calls
        mergeToolCallDelta(
          targetChoice.message.tool_calls,
          delta.tool_calls as ToolCall[]
        );
        // Only update UI if we have complete tool calls with valid JSON arguments
        const toolCallsAreValid = hasValidToolCalls(
          targetChoice.message.tool_calls
        );

        if (toolCallsAreValid) {
          shouldUpdate = true;
        }
      }

      if (shouldUpdate) {
        throttledUpdate();
      }
    });
  };

  /**
   * Cancels all active generation requests
   */
  const cancelGeneration = () => {
    // Abort all active requests
    abortControllersRef.current.forEach((controller, idx) => {
      controller.abort();
      setPlaygroundStateField(idx, 'loading', false);
    });
    // Clear all abort controllers
    abortControllersRef.current.clear();
  };

  return {handleStreamSend, handleRetryStream, cancelGeneration};
};

// Helper functions
const createMessage = (
  role: PlaygroundMessageRole,
  content: string,
  toolCallId?: string
): Message | undefined => {
  return content.trim() ? {role, content, tool_call_id: toolCallId} : undefined;
};

const handleMissingLLMApiKey = (responses: any, entity: string): boolean => {
  if (Array.isArray(responses)) {
    responses.forEach((response: any) => {
      handleMissingLLMApiKey(response, entity);
    });
  } else {
    if (responses?.api_key && responses.reason) {
      toast(
        <div>
          <div>{responses.reason}</div>
          Please add your API key to{' '}
          <Link to={`/${entity}/settings`}>Team secrets in settings</Link> to
          use this LLM
        </div>,
        {
          type: 'error',
        }
      );
      return true;
    }
  }
  return false;
};

const handleErrorResponse = (
  response: CompletionsCreateRes | null | {response: {error: string}}
): boolean => {
  if (!response) {
    return true;
  }
  if (response && 'error' in response.response) {
    toast(response.response?.error, {
      type: 'error',
    });
    return true;
  }

  return false;
};

// Helper function to check if an object is a Weave Content object
const isWeaveContent = (obj: any): obj is CustomWeaveTypePayload => {
  return (
    obj &&
    typeof obj === 'object' &&
    obj._type === 'CustomWeaveType' &&
    obj.weave_type &&
    obj.files &&
    typeof obj.files.content === 'string'
  );
};

const convertImageGenerationResponseToChatFormat = (response: any): any => {
  // Check if this is an image generation response
  if (response.response?.data && Array.isArray(response.response.data)) {
    // Process all images in the response
    const imageContents = response.response.data.map((imageData: any) => {
      // Handle Weave Content objects in b64_json field
      if (isWeaveContent(imageData.b64_json)) {
        return {
          type: 'image',
          image: imageData.b64_json, // Use the Weave content structure directly
        };
      }
      // Handle Weave Content objects in url field
      else if (isWeaveContent(imageData.url)) {
        return {
          type: 'image',
          image: imageData.url, // Use the Weave content structure directly
        };
      }
      // Handle string-based b64_json
      else if (imageData.b64_json && typeof imageData.b64_json === 'string') {
        let finalDataUrl: string;

        // Check if b64_json is already a data URL
        if (imageData.b64_json.startsWith('data:')) {
          finalDataUrl = imageData.b64_json;
        } else {
          finalDataUrl = `data:image/png;base64,${imageData.b64_json}`;
        }

        return {
          type: 'image_url',
          image_url: {
            url: finalDataUrl,
          },
        };
      }
      // Handle string-based URL
      else if (imageData.url && typeof imageData.url === 'string') {
        return {
          type: 'image_url',
          image_url: {
            url: imageData.url,
          },
        };
      } else {
        // Fallback to text description
        return {
          type: 'text',
          text: 'Generated image',
        };
      }
    });

    // Create a chat completion-style response
    const chatResponse = {
      id: `img-${Date.now()}`,
      object: 'chat.completion',
      created: response.response.created || Math.floor(Date.now() / 1000),
      model: 'image-generation',
      choices: [
        {
          index: 0,
          message: {
            role: 'assistant',
            content: imageContents,
          },
          finish_reason: 'stop',
        },
      ],
      usage: response.response.usage || {
        prompt_tokens: 0,
        completion_tokens: 0,
        total_tokens: 0,
      },
    };

    return {
      ...response,
      response: chatResponse,
    };
  }

  return response;
};

const handleUpdateCallWithResponse = (
  updatedCall: PlaygroundState,
  response: any
): PlaygroundState => {
  if (!response) {
    return updatedCall;
  }

  // Convert image generation responses to chat format
  const processedResponse =
    convertImageGenerationResponseToChatFormat(response);

  // Restore original template messages if they exist
  const originalTemplateMessages = (updatedCall.traceCall as any)
    ?.originalTemplateMessages;
  let finalMessages = updatedCall.traceCall?.inputs?.messages;

  if (originalTemplateMessages && Array.isArray(originalTemplateMessages)) {
    // Find the boundary between original template messages and new user messages
    const originalCount = originalTemplateMessages.length;
    const currentMessages = updatedCall.traceCall?.inputs?.messages || [];

    // Restore original template messages and preserve any new messages beyond the template
    if (currentMessages.length > originalCount) {
      // Keep original template + any new messages added after
      finalMessages = [
        ...originalTemplateMessages,
        ...currentMessages.slice(originalCount),
      ];
    } else {
      // Only original template messages exist
      finalMessages = originalTemplateMessages;
    }
  }

  return {
    ...updatedCall,
    traceCall: {
      ...clearTraceCall(updatedCall.traceCall),
      id: processedResponse.weave_call_id ?? '',
      output: processedResponse.response,
      inputs: {
        ...updatedCall.traceCall?.inputs,
        messages: finalMessages,
      },
    },
    loading: false,
  };
};

const appendChoiceToMessages = (
  state: PlaygroundState,
  choiceIndex?: number
): PlaygroundState => {
  const updatedState = JSON.parse(JSON.stringify(state));
  if (
    updatedState.traceCall?.inputs?.messages &&
    updatedState.traceCall.output?.choices
  ) {
    if (choiceIndex !== undefined) {
      updatedState.traceCall.inputs.messages.push(
        updatedState.traceCall.output.choices[choiceIndex].message
      );
    } else if (
      updatedState.traceCall.output.choices[updatedState.selectedChoiceIndex]
    ) {
      updatedState.traceCall.inputs.messages.push(
        updatedState.traceCall.output.choices[updatedState.selectedChoiceIndex]
          .message
      );
    }
    updatedState.traceCall.output.choices = undefined;
  }
  return updatedState;
};

/**
 * Filters out null messages from a PlaygroundState
 *
 * @param state The PlaygroundState to filter
 * @returns A new PlaygroundState with null messages filtered out
 */
export const filterNullMessages = (state: PlaygroundState): PlaygroundState => {
  if (
    !state.traceCall ||
    !state.traceCall.inputs ||
    !state.traceCall.inputs.messages
  ) {
    return state;
  }

  const messages = state.traceCall.inputs.messages as Message[];
  const filteredMessages = messages.filter(
    message =>
      message !== null &&
      typeof message === 'object' &&
      (message.content !== null || message.tool_calls !== null) &&
      message.content !== ''
  );

  // Only create a new state if messages were actually filtered out
  if (filteredMessages.length === messages.length) {
    return state;
  }

  return {
    ...state,
    traceCall: {
      ...state.traceCall,
      inputs: {
        ...state.traceCall.inputs,
        messages: filteredMessages,
      },
    },
  };
};

/**
 * Filters out null messages from an array of PlaygroundStates
 *
 * @param states Array of PlaygroundStates to filter
 * @returns A new array of PlaygroundStates with null messages filtered out
 */
export const filterNullMessagesFromStates = (
  states: PlaygroundState[]
): PlaygroundState[] => {
  return states.map(filterNullMessages);
};
